// The event photography pages (/event-photography and /event-photography/<slug>) for src/lib/seo/pages.ts: live
// everywhere. Each page is an Article with its images as ImageObjects, a BreadcrumbList and a FAQPage for the
// questions it shows; the hub is a CollectionPage listing them.

import { EVENT_INDEX_SEO, EVENT_PAGES, EVENT_PHOTOGRAPHY_PATH, eventPagePath } from "@/content/eventPhotography";
import { plainInline } from "@/content/guides/body";
import { getMedia } from "@/content/media";
import type { BlogPost } from "@/content/blog";
import { articleOgImage, articlePageImages } from "./blog";
import { imageObjects } from "./images";
import type { PublicPage, StructuredDataContext } from "./pages";

const SECTION = "event-photography";

const crumbs = (url: string, items: { name: string; url: string }[]) => ({
  "@type": "BreadcrumbList",
  "@id": `${url}#breadcrumbs`,
  itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: it.url })),
});

export const HUB_IMAGE = "event-gallery-photo-grid";

export const eventPageStructuredData = (p: BlogPost, { origin, url }: StructuredDataContext): object[] => [
  {
    "@type": "Article",
    "@id": `${url}#article`,
    mainEntityOfPage: url,
    url,
    headline: p.title,
    description: plainInline(p.description),
    inLanguage: "en",
    image: imageObjects(origin, articleOgImage(p, SECTION), articlePageImages(p)),
    thumbnailUrl: `${origin}${articleOgImage(p, SECTION).path}`,
    datePublished: p.published,
    dateModified: p.updated ?? p.published,
    author: { "@type": "Organization", name: "Photohawk team", url: `${origin}/` },
    publisher: { "@id": `${origin}/#organization` },
    isPartOf: { "@type": "CollectionPage", "@id": `${origin}${EVENT_PHOTOGRAPHY_PATH}#page` },
    wordCount: p.words,
  },
  crumbs(url, [
    { name: "Home", url: `${origin}/` },
    { name: "Event photography", url: `${origin}${EVENT_PHOTOGRAPHY_PATH}` },
    { name: p.title, url },
  ]),
  {
    "@type": "FAQPage",
    "@id": `${url}#faq`,
    mainEntity: p.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: plainInline(f.a) } })),
  },
];

/** /event-photography and its pages. */
export function eventPhotographyPages(): PublicPage[] {
  const hubImage = getMedia(HUB_IMAGE);
  const latest = EVENT_PAGES.map((p) => p.updated ?? p.published).reduce((a, b) => (a > b ? a : b), "");
  const hubOg = { path: "/images/og/pages/event-photography.jpg", width: 1200, height: 630, type: "image/jpeg", alt: "Event photography guides for sports and organisers. Photohawk Studio." };
  const hubImages = hubImage ? [{ path: hubImage.src, width: hubImage.width, height: hubImage.height, alt: hubImage.alt, caption: hubImage.caption }] : [];
  const hub: PublicPage = {
    path: EVENT_PHOTOGRAPHY_PATH,
    langs: ["en"],
    seo: () => EVENT_INDEX_SEO,
    image: hubOg,
    images: hubImages,
    type: "website",
    indexable: true,
    changefreq: "monthly",
    priority: 0.6,
    lastmod: latest || undefined,
    structuredData: ({ origin, url, title, description }) => [
      {
        "@type": "CollectionPage",
        "@id": `${url}#page`,
        url,
        name: title,
        description,
        inLanguage: "en",
        image: imageObjects(origin, hubOg, hubImages),
        isPartOf: { "@type": "WebSite", "@id": `${origin}/#website`, name: "Photohawk", url: `${origin}/` },
        publisher: { "@id": `${origin}/#organization` },
        mainEntity: {
          "@type": "ItemList",
          itemListElement: EVENT_PAGES.map((p, i) => ({ "@type": "ListItem", position: i + 1, url: `${origin}${eventPagePath(p.slug)}`, name: p.title })),
        },
      },
      crumbs(url, [
        { name: "Home", url: `${origin}/` },
        { name: "Event photography", url },
      ]),
    ],
  };
  return [
    hub,
    ...EVENT_PAGES.map(
      (p): PublicPage => ({
        path: eventPagePath(p.slug),
        langs: ["en"],
        seo: () => ({ title: `${p.metaTitle} | Photohawk`.length <= 60 ? `${p.metaTitle} | Photohawk` : p.metaTitle, description: p.description }),
        image: articleOgImage(p, SECTION),
        images: articlePageImages(p),
        type: "article",
        indexable: true,
        changefreq: "monthly",
        priority: 0.5,
        lastmod: p.updated ?? p.published,
        structuredData: (ctx) => eventPageStructuredData(p, ctx),
      }),
    ),
  ];
}
